import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Wrench, Eye, EyeOff, QrCode, Mail, Loader2 } from 'lucide-react';
import { useQrScanner } from '../hooks/useQrScanner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithQR } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [activeTab, setActiveTab] = useState('email');
  const [scannerReady, setScannerReady] = useState(false);
  const scannerRef = useRef(null);
  const { start: startQr, stop: stopQr } = useQrScanner();

  useEffect(() => {
    return () => { stopQr(); };
  }, []);

  useEffect(() => {
    if (activeTab === 'qr') {
      startScanner();
    } else {
      stopScanner();
    }
    return () => stopScanner();
  }, [activeTab]);

  const startScanner = async () => {
    if (!scannerRef.current) return;
    try {
      await new Promise(r => setTimeout(r, 300));
      await startQr('qr-login-reader', (decodedText) => handleQRScan(decodedText), { fps: 10, qrbox: { width: 250, height: 250 } });
      setScannerReady(true);
    } catch (err) {
      console.error("Scanner error:", err);
      setScannerReady(false);
    }
  };

  const stopScanner = async () => {
    await stopQr();
    setScannerReady(false);
  };

  const handleQRScan = async (token) => {
    stopScanner();
    setLoading(true);
    try {
      const badgeToken = token.startsWith('MX-') ? token : token;
      const userData = await loginWithQR(badgeToken);
      toast.success(`Bine ai venit, ${userData.full_name}!`);
      if (userData.role === 'auditor') {
        navigate('/audit');
      } else if (userData.role === 'section_chief') {
        navigate('/portal');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ecuson invalid sau expirat');
      setActiveTab('email');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      toast.success('Autentificare reusita!');
      if (userData.role === 'auditor') {
        navigate('/audit');
      } else if (userData.role === 'section_chief') {
        navigate('/portal');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la autentificare');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1684259499227-e9844ab79747?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMHdvcmtlciUyMHRhYmxldHxlbnwwfHx8fDE3NjkwNjc5NDd8MA&ixlib=rb-4.1.0&q=85')`,
          transform: 'scale(1.05)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/80 to-slate-900/90" />

      <div className="relative z-10 w-full max-w-md" data-testid="login-container">
        <Card className="border-0 shadow-2xl backdrop-blur-sm bg-white/95" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-[#0052CC] rounded-xl flex items-center justify-center shadow-lg">
                <Wrench className="w-10 h-10 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">MAINTIX</CardTitle>
            <CardDescription className="text-slate-600">Smart Maintenance & Asset Control</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-slate-100 rounded-lg p-1" data-testid="login-tabs">
              <button
                type="button"
                onClick={() => setActiveTab('email')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'email'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="tab-email"
              >
                <Mail className="w-4 h-4" />
                Email & Parola
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('qr')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'qr'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="tab-qr"
              >
                <QrCode className="w-4 h-4" />
                Scanare Ecuson
              </button>
            </div>

            {/* Email & Password Tab */}
            {activeTab === 'email' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="email@companie.ro"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="h-12"
                    data-testid="login-email"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Parola</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-[#0052CC] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Am uitat parola
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="--------"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="h-12 pr-12"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 btn-primary"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'SE PROCESEAZA...' : 'CONECTARE'}
                </Button>
              </form>
            )}

            {/* QR Scan Tab */}
            {activeTab === 'qr' && (
              <div className="space-y-4" data-testid="qr-login-section">
                <div className="text-center text-sm text-slate-600 mb-2">
                  Pozitionati camera pe codul QR al ecusonului
                </div>
                <div className="relative rounded-xl overflow-hidden border-2 border-dashed border-slate-300 bg-slate-50" style={{ minHeight: '280px' }}>
                  <div id="qr-login-reader" ref={scannerRef} className="w-full" />
                  {loading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 text-[#0052CC] animate-spin mx-auto mb-2" />
                        <p className="text-sm text-slate-600">Se autentifica...</p>
                      </div>
                    </div>
                  )}
                  {!scannerReady && !loading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <QrCode className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-400">Se porneste camera...</p>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-center text-xs text-slate-500">
                  Ecusonul QR este generat de administrator in sectiunea Ecusoane
                </p>
              </div>
            )}

            <p className="text-center text-sm text-slate-500 mt-4">
              Contactati administratorul pentru a obtine un cont.
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-white/50 mt-6 tracking-wide" data-testid="developer-credit">
          Conceput si dezvoltat de <span className="text-white/70 font-medium">Daniel Morosanu</span>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
